import clsx from 'clsx';
import React, { type MouseEvent, useEffect, useRef, useState } from 'react';
import { QueryClient } from 'react-query';

import { SecondaryButton } from '@components/Button/variants';
import FlexLink from '@components/FlexLink';
import IconPlusWithCircle from '@components/Images/Rebranding/IconPlusWithCircle';
import LazyImgixImage from '@components/LazyImgixImage';
import useEventTracking from '@hooks/useEventTracking';
import useFollowUpdate from '@hooks/useFollowUpdate';
import { scrollToHashSection } from '@hooks/useScrollToHash';
import { ClickTarget, click } from '@tracking';
import { FollowAction, FollowType } from '@utils/backend-api/follow';
import { getImgixSrc } from '@utils/images';

import {
  ANCHOR_LINKS,
  HERO_IMAGES_SLIDE_SHOW,
  SUBSCRIBE_TO_EVENTS_TEXT,
  UNSUBSCRIBE_FROM_EVENTS_TEXT,
} from './constant';

interface SlideShowProps {
  eventEntityId: number;
  followStatus: boolean;
  isCurrentUserSubscriber: boolean;
  queryClient: QueryClient;
}
const AUTO_ADVANCE_MS = 5500;

const SlideShow = ({
  eventEntityId,
  followStatus,
  isCurrentUserSubscriber,
  queryClient,
}: SlideShowProps) => {
  const anchorCount = ANCHOR_LINKS.length;
  const lastAnchorIndex = anchorCount - 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFollowing, setIsFollowing] = useState(followStatus);
  const { mutateAsync: updateFollow } = useFollowUpdate();
  const prefersReducedMotionRef = useRef(false);
  const total = HERO_IMAGES_SLIDE_SHOW.length;
  const { trackEvent } = useEventTracking();

  const handleAnchorNavClick = (e: MouseEvent<HTMLAnchorElement>, hash: string, name: string) => {
    e.preventDefault();
    scrollToHashSection(hash);
    const next = `${window.location.pathname}${window.location.search}#${hash}`;
    if (window.location.hash !== `#${hash}`) {
      window.history.pushState(null, '', next);
    }
    trackEvent(click({ clickTarget: ClickTarget.AnchorLink, clickText: name }));
  };

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotionRef.current = mq.matches;

    let intervalId: number | undefined;

    const syncAutoAdvance = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
      if (total <= 1 || prefersReducedMotionRef.current) return;
      intervalId = window.setInterval(() => {
        setActiveIndex((i) => (i + 1) % total);
      }, AUTO_ADVANCE_MS);
    };

    syncAutoAdvance();

    const onPreferenceChange = () => {
      prefersReducedMotionRef.current = mq.matches;
      syncAutoAdvance();
    };

    mq.addEventListener('change', onPreferenceChange);
    return () => {
      mq.removeEventListener('change', onPreferenceChange);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [total]);

  const handleUpdateFollowStatus = async () => {
    const { success } = await updateFollow({
      followeeId: eventEntityId,
      followAction: isFollowing ? FollowAction.Unfollow : FollowAction.Follow,
      followType: FollowType.Entity,
      queryClient,
    });

    if (success) {
      setIsFollowing(!isFollowing);
    }
  };

  return (
    <div className="relative mx-auto flex w-full max-w-[1728px] flex-col sm:block">
      <div className="mb-32 mt-32 flex flex-col items-center justify-center text-center !text-black sm:absolute sm:left-1/2 sm:top-1/2 sm:z-10 sm:mb-0 sm:mt-0 sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:px-16 sm:text-headline-alt-md sm:!text-white">
        <nav className="max-w-fit border-0 border-b-[1.5px] border-solid border-black pb-4 sm:border-white">
          <ul className="m-0 flex list-none items-center gap-24 p-0">
            {ANCHOR_LINKS.map(({ name, hash }, index) => (
              <li key={name}>
                <FlexLink
                  href={`#${hash}`}
                  onClickCapture={(e) => handleAnchorNavClick(e, hash, name)}
                  className={clsx(
                    'relative block border-none bg-transparent pb-8 font-suisse-intl-mono text-overline-lg text-black transition-colors hover:!text-primary-magenta sm:!text-white ',
                    index > 0 &&
                      'before:absolute before:-left-36 before:bottom-0 before:h-px before:w-36 before:origin-bottom-right before:rotate-[50deg] before:bg-black before:content-[""] before:sm:bg-white',
                    index === 0 && lastAnchorIndex > 0 && 'pr-16 text-left',
                    index === 1 && 'px-16',
                    index === lastAnchorIndex && lastAnchorIndex > 0 && 'pl-16 text-right'
                  )}
                >
                  {name}
                </FlexLink>
              </li>
            ))}
          </ul>
        </nav>
        <h1
          className={clsx(
            'mb-8 mt-32 px-16 text-center font-suisse-screen !text-black sm:!text-white',
            'text-headline-alt-md sm:text-headline-alt-lg lg:text-headline-alt-xl'
          )}
        >
          The Information Events
        </h1>
        <p className="m-0 font-suisse-works text-body-md text-black sm:!text-white md:text-body-lg lg:text-headline-xs">
          Be in the room where the future of tech is decided.
        </p>

        {isCurrentUserSubscriber && (
          <SecondaryButton
            as="button"
            className={clsx(
              'group mt-32 max-w-fit rounded-[100px] !border-grayscale-gray bg-white px-8 py-8 hover:!bg-primary-magenta focus:bg-white focus:text-primary-magenta enabled:hover:!bg-primary-magenta sm:mt-64'
            )}
            data-test="follow-button"
            onClick={handleUpdateFollowStatus}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickTarget: ClickTarget.SubscribeToEvents,
                  clickText: isFollowing ? UNSUBSCRIBE_FROM_EVENTS_TEXT : SUBSCRIBE_TO_EVENTS_TEXT,
                  contentId: eventEntityId,
                })
              )
            }
          >
            <div className="flex gap-8 group-hover:text-white">
              {!isFollowing ? (
                <>
                  <IconPlusWithCircle className="fill-primary-magenta stroke-white group-hover:fill-white group-hover:stroke-primary-magenta" />
                  <div className="font-suisse-intl text-button-sm group-hover:text-white">
                    Get event updates
                  </div>
                </>
              ) : (
                <div className="font-suisse-intl text-button-sm group-hover:text-white">
                  Unsubscribe from events
                </div>
              )}
            </div>
          </SecondaryButton>
        )}
      </div>
      <div
        className="relative h-[220px] w-full overflow-hidden bg-neutral-900 xs:h-[432px] sm:h-[557px] lg:h-[799px]"
        role="region"
        aria-roledescription="carousel"
        aria-label="Event photos"
      >
        {HERO_IMAGES_SLIDE_SHOW.map((image, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={image}
              className={clsx(
                'absolute inset-0',
                'motion-reduce:transition-none motion-reduce:duration-0',
                'transition-opacity duration-700 ease-in-out',
                isActive ? 'z-[1] opacity-100' : 'z-0 opacity-0'
              )}
              aria-hidden={!isActive}
            >
              <LazyImgixImage
                src={getImgixSrc({ path: image, isEnvDependent: false })}
                alt=""
                className="block h-full min-h-full w-full object-cover object-center"
                sizes="100vw"
                visibleByDefault={index === 0}
                wrapperClassName="!block h-full min-h-0 w-full leading-[0]"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SlideShow;
